import { useRouterState } from "@tanstack/react-router";

import { navItems, pages, platformRole, type NavGroup } from "@/app/registry";
import { useSession } from "@/app/session";
import { brand } from "@/brand";

import { UNGATED, usePlanInactive } from "./planAccess";

type Item = ReturnType<typeof navItems>[number];

/** What each section is called in the top bar. */
export const SECTION_LABEL: Record<NavGroup, string> = {
  Markets: "Markets",
  Research: "Research",
  // the self-built Pro tools (screener, options, backtests...); a new tool
  // joins by setting nav.group "Premium". An RA's client sees none of them.
  Premium: "Premium tools",
  // the AI paper trades: admins and users ticked for AI only (brand.json)
  AI: `${brand.name} AI`,
  // the SEBI registers' directories, and RA calls (My Feed, Explore) since V063
  Analysts: "Research Analysts",
  // a Research Analyst's own tools: publish, my calls, clients, registration
  Practice: "RA desk",
  System: "Account",
};

/** Section names for the signed-in user: admins see the System section as "Admin"
 * (admin console, data health, account); everyone else as "Account". */
export function useSectionLabels(): Record<NavGroup, string> {
  const { user } = useSession();
  const role = platformRole(user?.role);
  const system =
    role === "admin" ? "Admin" : role === "employee" ? "Sales" : "Account";
  return { ...SECTION_LABEL, System: system };
}

/** The signed-in user's pages grouped into sections, in nav order. */
export function useSections(): Map<NavGroup, Item[]> {
  const { user } = useSession();
  const inactive = usePlanInactive();
  const all = navItems(
    pages,
    user?.role,
    user?.ai_calls_access === true,
    user?.access_tier ?? "full",
    user?.ra_client === true,
  );
  // a plan that isn't active opens only Account and Payment
  const items = inactive ? all.filter((p) => UNGATED.includes(p.path)) : all;
  const sections = new Map<NavGroup, Item[]>();
  for (const it of items)
    sections.set(it.nav.group, [...(sections.get(it.nav.group) ?? []), it]);
  return sections;
}

/** The section the URL belongs to: the page with the longest matching path
 * ("/app/stocks/RELIANCE" counts under "/app/stocks"). */
export function sectionOf(
  sections: Map<NavGroup, Item[]>,
  path: string,
): NavGroup | undefined {
  let best: { group: NavGroup; len: number } | undefined;
  for (const [group, entries] of sections) {
    for (const e of entries) {
      for (const p of [e.path, e.path.split("/").slice(0, 3).join("/")]) {
        if (
          (path === p || path.startsWith(`${p}/`)) &&
          (!best || p.length > best.len)
        )
          best = { group, len: p.length };
      }
    }
  }
  return best?.group;
}

/** The current section and its pages (tabs show only when there are two or more). */
export function useSubNav() {
  const sections = useSections();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const current = sectionOf(sections, path);
  const entries = current ? (sections.get(current) ?? []) : [];
  return { current, entries, path, show: !!current && entries.length > 1 };
}

/** A section that stands out in the menus (the "<brand> AI" one): any of its pages is highlighted. */
export function highlighted(entries: Item[]): boolean {
  return entries.some((e) => e.nav.highlight);
}
